import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import {
  Award,
  ShieldCheck,
  Sparkles,
  FileText,
  MessageCircle,
  UserMinus,
  Lightbulb,
  GraduationCap,
  BadgeCheck,
  UserPlus,
} from "lucide-react";
import Header from "../components/layout/Header";
import GettingStartedChecklist from "../components/GettingStartedChecklist";
import InviteStudentModal from "../components/modals/InviteStudentModal";
import ImportRosterModal from "../components/modals/ImportRosterModal";
import InviteStaffModal from "../components/modals/InviteStaffModal";
import WelcomeScreen from "../components/WelcomeScreen";
import { studentService } from "../services/studentService";
import { certificateService } from "../services/certificateService";
import { useSchoolRole, canManageStaff } from "../hooks/useSchoolRole";
import goal from "../assets/goal.png";
import group from "../assets/group2.svg";
import Pending from "../assets/pending.svg";

const GETTING_STARTED_DISMISS_KEY = "hypebook.schoolAdmin.gettingStartedDismissed";
const GETTING_STARTED_STAFF_SKIPPED_KEY = "hypebook.schoolAdmin.gettingStartedStaffSkipped";
// Client-side "profile confirmed" acknowledgement (no backend flag yet) — set
// when the admin clicks Confirm profile on the first-run checklist.
const GETTING_STARTED_PROFILE_KEY = "hypebook.schoolAdmin.gettingStartedProfileConfirmed";
// First-run welcome modal (shown once, then dismissed). localStorage to match
// the Getting-Started checklist's client-side persistence.
const WELCOME_SHOWN_KEY = "hypebook.schoolAdmin.welcomeShown";

// Staff roles in rank order (high → low) with their i18n label keys (reused
// from the invite-staff modal). Drives the role breakdown on the team card.
const STAFF_ROLE_LABELS = [
  ["OWNER", "modals.inviteStaff.roles.owner"],
  ["BILLING", "modals.inviteStaff.roles.billing"],
  ["ADMIN", "modals.inviteStaff.roles.admin"],
  ["COUNSELOR", "modals.inviteStaff.roles.counselor"],
  ["CLASSROOM_VIEWER", "modals.inviteStaff.roles.classroom_viewer"],
];

// Count a number up from 0 on mount/change — a small moment of delight as the
// dashboard data lands. Respects prefers-reduced-motion and skips zero.
const AnimatedNumber = ({ value }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = Number(value) || 0;
    const reduce =
      typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || target === 0) {
      setDisplay(target);
      return;
    }
    const duration = 650;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - p) ** 3; // easeOutCubic
      setDisplay(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{display.toLocaleString()}</>;
};

// Warm first-run hero — shown in place of the wall-of-zeros stat grid when a
// school has no students or invites yet. Frames the empty dashboard as a
// beginning (what's coming + a clear first action) rather than a graveyard of
// 0s.
const EmptyDashboardHero = ({ onInvite, onImport, onShareCode, onInviteStaff, hasTeam, staffSkipped }) => {
  const { t } = useTranslation();
  const previews = [
    { icon: Award, tone: "bg-blue-50 text-blue-600", label: t("dashboard.empty.p1", { defaultValue: "Achievements" }) },
    { icon: ShieldCheck, tone: "bg-emerald-50 text-emerald-600", label: t("dashboard.empty.p2", { defaultValue: "Verifications" }) },
    { icon: FileText, tone: "bg-violet-50 text-violet-600", label: t("dashboard.empty.p3", { defaultValue: "Resumes" }) },
  ];
  // Staff before students: until a team exists, the hero leads with inviting
  // colleagues (matching step 1 of the getting-started checklist) so the two
  // don't give contradictory "do this first" instructions. Student actions
  // stay available as secondary, and become primary once a team is in place.
  // A solo admin who chose "It's just me" is never nagged again.
  const teamFirst = !hasTeam && !staffSkipped;
  return (
    <div className="rounded-3xl border border-blue-100 bg-blue-50/40 p-8 md:p-10">
      <div className="flex flex-col gap-5 max-w-2xl">
        <div>
          <h3 className="text-2xl font-bold text-gray-900">
            {teamFirst
              ? t("dashboard.empty.titleTeam", { defaultValue: "Start by adding your team" })
              : t("dashboard.empty.title", { defaultValue: "Invite your first students" })}
          </h3>
          <p className="mt-1.5 text-gray-600 text-base">
            {teamFirst
              ? t("dashboard.empty.subtitleTeam", {
                  defaultValue:
                    "Invite counselors and teachers first so everyone has the right role before students arrive — then bring your students in together.",
                })
              : t("dashboard.empty.subtitle", {
                  defaultValue:
                    "Your dashboard fills with achievements, verifications and resumes as students get going. Start by inviting them.",
                })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {teamFirst ? (
            <button
              type="button"
              onClick={onInviteStaff}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-600 transition-colors"
            >
              <UserPlus size={16} />
              {t("manageStudents.inviteStaff", { defaultValue: "Invite Staff" })}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onInvite}
            className={
              teamFirst
                ? "inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:border-primary hover:text-primary transition-colors"
                : "inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-600 transition-colors"
            }
          >
            <UserPlus size={16} />
            {t("manageStudents.inviteStudent", { defaultValue: "Invite students" })}
          </button>
          <button
            type="button"
            onClick={onImport}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:border-primary hover:text-primary transition-colors"
          >
            <FileText size={16} />
            {t("manageStudents.importRoster", { defaultValue: "Import roster" })}
          </button>
          <button type="button" onClick={onShareCode} className="text-sm font-semibold text-primary hover:underline px-2">
            {t("dashboard.empty.shareCode", { defaultValue: "or share your join code" })}
          </button>
        </div>
        <div className="mt-1 flex flex-wrap gap-2">
          {previews.map(({ icon: Icon, tone, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600"
            >
              <span className={`flex h-5 w-5 items-center justify-center rounded-md ${tone}`}>
                <Icon size={12} />
              </span>
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Headline roster stat (image-icon variant) — moved here from ManageStudents.
const StatCard = ({ icon, label, value, color, onClick }) => {
  const base = `p-6 bg-white rounded-2xl outline outline-1 outline-${color} flex flex-col gap-6`;
  const inner = (
    <>
      <img src={icon} alt={label} className="w-10 h-10" />
      <div className="flex justify-between items-end">
        <span className="text-gray-900 text-lg font-medium">{label}</span>
        <span className="text-gray-900 text-[40px] font-medium leading-10">
          {typeof value === "number" ? <AnimatedNumber value={value} /> : value}
        </span>
      </div>
    </>
  );
  // Clickable variant (deep-links to the Manage Students sub-page); plain
  // div when no handler is supplied.
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${base} w-full text-left cursor-pointer hover:shadow-md hover:outline-primary transition-all`}>
        {inner}
      </button>
    );
  }
  return <div className={base}>{inner}</div>;
};

// Compact engagement metric — moved here from ManageStudents.
const EngagementStat = ({ icon: Icon, label, value, sub, tone = "blue", onClick }) => {
  const toneClasses = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  }[tone];
  const content = (
    <>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${toneClasses}`}>
        <Icon className="w-[18px] h-[18px]" />
      </div>
      <div className="min-w-0 text-left">
        <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {sub ? <p className="text-xs text-gray-400 mt-0.5">{sub}</p> : null}
      </div>
    </>
  );
  // Clickable cards drill into the Engagement page (pre-filtered). Static
  // when no handler is given.
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex items-start gap-3 px-4 py-3.5 rounded-xl border border-gray-100 bg-white text-left transition-colors hover:border-primary hover:bg-blue-50/40 focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        {content}
      </button>
    );
  }
  return <div className="flex items-start gap-3 px-4 py-3.5 rounded-xl border border-gray-100 bg-white">{content}</div>;
};

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalAchievements: 0,
    activeStudents: 0,
    pendingInvites: 0,
    schoolName: "",
    adminName: "",
    totalStudents: 0,
    studentsWithAchievements: 0,
    studentsWithHypepage: 0,
    studentsWithResume: 0,
    verifiedAchievements: 0,
    hypesReceived: 0,
    cohorts: [],
  });
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [schoolJoinInfo, setSchoolJoinInfo] = useState(null);
  const [certIssued, setCertIssued] = useState(false);
  const [hasTeam, setHasTeam] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [pendingStaffCount, setPendingStaffCount] = useState(0);
  const [showInviteStaffModal, setShowInviteStaffModal] = useState(false);

  // Team card is ADMIN+ only, matching the staff-management gate elsewhere.
  // Fails open on an unknown role so it isn't hidden from an admin when the
  // role can't be resolved (e.g. older API).
  const schoolRole = useSchoolRole();
  const showTeamCard = canManageStaff(schoolRole);

  const [gsDismissed, setGsDismissed] = useState(() => localStorage.getItem(GETTING_STARTED_DISMISS_KEY) === "1");
  const [gsManualOpen, setGsManualOpen] = useState(false);
  const [staffSkipped, setStaffSkipped] = useState(() => localStorage.getItem(GETTING_STARTED_STAFF_SKIPPED_KEY) === "1");
  const [profileConfirmed, setProfileConfirmed] = useState(() => localStorage.getItem(GETTING_STARTED_PROFILE_KEY) === "1");

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showImportRosterModal, setShowImportRosterModal] = useState(false);
  // First-run gate: show once, then localStorage-silence it. `?welcome=1` forces
  // it back on regardless (handy for testing/demos — works on deploy too); the
  // flag is otherwise untouched, so a normal reload hides it again.
  const [showWelcome, setShowWelcome] = useState(() => {
    if (new URLSearchParams(window.location.search).get("welcome") === "1") return true;
    return localStorage.getItem(WELCOME_SHOWN_KEY) !== "1";
  });

  const fetchStats = useCallback(async () => {
    try {
      const response = await studentService.getDashboardStats();
      const adminName = [response.adminFirstName, response.adminLastName].filter(Boolean).join(" ");
      setStats({
        totalAchievements: response.totalAchievements || 0,
        activeStudents: response.activeStudents || 0,
        pendingInvites: response.pendingInvites || 0,
        schoolName: response.schoolName || "",
        adminName,
        totalStudents: response.totalStudents || 0,
        studentsWithAchievements: response.studentsWithAchievements || 0,
        studentsWithHypepage: response.studentsWithHypepage || 0,
        studentsWithResume: response.studentsWithResume || 0,
        verifiedAchievements: response.verifiedAchievements || 0,
        hypesReceived: response.hypesReceived || 0,
        cohorts: Array.isArray(response.cohorts) ? response.cohorts : [],
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setStatsLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await studentService.getSchoolJoinInfo();
        if (!cancelled) setSchoolJoinInfo(response);
      } catch (error) {
        console.error("Error fetching school join info:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cheap probes that drive the Getting-started steps.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await certificateService.listCertificates({ page: 1, pageSize: 1 });
        if (!cancelled) setCertIssued((res?.total || 0) > 0);
      } catch {
        /* leave certIssued=false */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull the team (active memberships + pending invites). Drives both the
  // getting-started `hasTeam` signal and the team summary card. Memoized so
  // it can be reused after an invite without duplicating the fetch.
  const refreshTeam = useCallback(async () => {
    const [members, pending] = await Promise.all([
      studentService.getSchoolMemberships().catch(() => []),
      studentService.getPendingStaffInvites().catch(() => []),
    ]);
    const memberList = Array.isArray(members) ? members : [];
    const pendingList = Array.isArray(pending) ? pending : [];
    setStaffMembers(memberList);
    setPendingStaffCount(pendingList.length);
    setHasTeam(memberList.length > 1 || pendingList.length > 0);
  }, []);

  useEffect(() => {
    void refreshTeam();
  }, [refreshTeam]);

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("manageStudents.toasts.copied", { label }));
    } catch {
      toast.error(t("manageStudents.toasts.failedCopy", { label: label.toLowerCase() }));
    }
  };

  const handleInviteStudent = async (emails) => {
    try {
      await studentService.inviteStudents(emails);
      toast.success(t("manageStudents.toasts.invitedStudents", { count: emails.length }));
      fetchStats();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedInviteStudents"));
    }
    setShowInviteModal(false);
  };

  const handleInviteStaff = async (payload) => {
    try {
      await studentService.inviteSchoolStaff(payload);
      toast.success(t("manageStudents.toasts.staffInviteSent"));
      await refreshTeam();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedInviteStaff"));
    }
    setShowInviteStaffModal(false);
  };

  // ── Getting-started visibility ───────────────────────────────────────
  // Core setup (profile + team + students) makes the school "live"; the cert is a
  // post-launch nudge. Keep the card visible until the cert is issued too, so the
  // "What's next" nudge gets a chance to land — then it auto-hides.
  //
  // Sub-admin staff run a different journey (students + roster + certificate) —
  // they can't confirm the school profile or add team, so gating on those would
  // pin their checklist open forever.
  const hasAnyStudents = (stats.totalStudents || 0) > 0 || (stats.pendingInvites || 0) > 0;
  const gettingStartedComplete = showTeamCard
    ? profileConfirmed && hasAnyStudents && (hasTeam || staffSkipped) && certIssued
    : hasAnyStudents && certIssued;
  const showGettingStarted = statsLoaded && (gsManualOpen || (!gsDismissed && !gettingStartedComplete));

  const handleOpenGettingStarted = () => {
    localStorage.removeItem(GETTING_STARTED_DISMISS_KEY);
    setGsDismissed(false);
    setGsManualOpen(true);
    setTimeout(() => {
      document.getElementById("getting-started")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleDismissGettingStarted = () => {
    localStorage.setItem(GETTING_STARTED_DISMISS_KEY, "1");
    setGsDismissed(true);
    setGsManualOpen(false);
  };

  const handleSkipTeam = () => {
    localStorage.setItem(GETTING_STARTED_STAFF_SKIPPED_KEY, "1");
    setStaffSkipped(true);
  };

  // "Confirm profile" — optimistic acknowledgement (the checklist also routes
  // to Settings so they actually review it). Persisted so it stays checked.
  const handleConfirmProfile = () => {
    localStorage.setItem(GETTING_STARTED_PROFILE_KEY, "1");
    setProfileConfirmed(true);
  };

  // ── First-run welcome ────────────────────────────────────────────────
  const dismissWelcome = () => {
    localStorage.setItem(WELCOME_SHOWN_KEY, "1");
    setShowWelcome(false);
  };
  // Primary welcome CTA: dismiss the takeover and drop the admin into the
  // Getting-Started checklist (the setup hub — invite is its first step).
  const handleWelcomeStart = () => {
    dismissWelcome();
    setTimeout(() => {
      document.getElementById("getting-started")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const scrollToJoin = () =>
    document.getElementById("school-join")?.scrollIntoView({ behavior: "smooth", block: "center" });

  // ── Data-driven nudges (computed from stats we already have) ─────────
  const yetToStart = Math.max(0, (stats.totalStudents || 0) - (stats.studentsWithAchievements || 0));
  const verifiedPct = stats.totalAchievements > 0 ? Math.round((stats.verifiedAchievements / stats.totalAchievements) * 100) : null;
  const tips = [];
  if (stats.totalStudents > 0 && yetToStart > 0) {
    tips.push({
      key: "yetToStart",
      text: t("dashboard.tips.yetToStart", {
        defaultValue: "{{count}} student(s) haven't logged anything yet — share your join code or give them a nudge.",
        count: yetToStart,
      }),
      action: { label: t("dashboard.tips.shareCode", { defaultValue: "Share join code" }), onClick: scrollToJoin },
    });
  }
  if (stats.pendingInvites > 0) {
    tips.push({
      key: "pending",
      text: t("dashboard.tips.pending", {
        defaultValue: "{{count}} invite(s) still pending — resend any that are going stale.",
        count: stats.pendingInvites,
      }),
      action: { label: t("dashboard.tips.viewInvites", { defaultValue: "View invites" }), onClick: () => navigate("/manage-students?tab=pending") },
    });
  }
  if (verifiedPct !== null && verifiedPct < 50 && stats.totalAchievements > 0) {
    tips.push({
      key: "verify",
      // 0% reads awkwardly as a percentage ("Only 0% …"); use plain language
      // when nothing's been verified yet, and the percentage otherwise.
      text:
        verifiedPct === 0
          ? t("dashboard.tips.verifyNone", {
              defaultValue: "None of your students' achievements have been verified yet — remind them to request verifications.",
            })
          : t("dashboard.tips.verify", {
              defaultValue: "Only {{pct}}% of achievements are verified — remind students to request verifications.",
              pct: verifiedPct,
            }),
    });
  }
  const visibleTips = tips.slice(0, 3);

  // ── Team summary (active staff + role breakdown) ─────────────────────
  const activeStaff = staffMembers.filter((member) => member.status === "ACTIVE");
  const roleCounts = activeStaff.reduce((acc, member) => {
    acc[member.role] = (acc[member.role] || 0) + 1;
    return acc;
  }, {});

  // ── Warm greeting + empty-state framing ──────────────────────────────
  const firstName = (stats.adminName || "").trim().split(/\s+/)[0] || "";
  const hour = new Date().getHours();
  const greetingWord =
    hour < 12
      ? t("dashboard.greeting.morning", { defaultValue: "Good morning" })
      : hour < 18
        ? t("dashboard.greeting.afternoon", { defaultValue: "Good afternoon" })
        : t("dashboard.greeting.evening", { defaultValue: "Good evening" });
  const greeting = firstName ? `${greetingWord}, ${firstName}` : greetingWord;
  // Brand-new school (nothing started) → show the welcoming hero instead of a
  // grid of zeros. Once there are students OR pending invites, show real stats.
  const isEmptySchool = statsLoaded && (stats.totalStudents || 0) === 0 && (stats.pendingInvites || 0) === 0;

  // Hold a neutral full-screen loader until stats resolve. Without this the
  // dashboard first renders its populated layout (with zero values), then swaps
  // to the empty-school hero + welcome overlay once stats land — and for a brand
  // new admin who just accepted an invite, that swap reads as the empty
  // dashboard flashing for an instant before the welcome takeover appears.
  // A clean spinner → welcome (or → dashboard) avoids the flash entirely.
  if (!statsLoaded) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center font-manrope">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Primary above-the-fold block: the invite CTAs (brand-new school) or the
  // headline roster stats. Rendered in the left column beside the compact
  // Getting-Started rail, or full-width when the checklist is hidden.
  const primaryBlock = isEmptySchool ? (
    <EmptyDashboardHero
      onInvite={() => setShowInviteModal(true)}
      onImport={() => setShowImportRosterModal(true)}
      onShareCode={scrollToJoin}
      onInviteStaff={() => setShowInviteStaffModal(true)}
      hasTeam={hasTeam}
      staffSkipped={staffSkipped}
    />
  ) : (
    <div className="grid grid-cols-3 gap-4">
      <StatCard
        icon={goal}
        label={t("manageStudents.stats.totalStudents", { defaultValue: "Total Students" })}
        value={stats.totalStudents}
        color="primary"
        onClick={() => navigate("/manage-students")}
      />
      <StatCard
        icon={group}
        label={t("manageStudents.stats.activeStudents")}
        value={stats.activeStudents}
        color="blue-500"
        onClick={() => navigate("/manage-students")}
      />
      <StatCard
        icon={Pending}
        label={t("manageStudents.stats.pendingInvites")}
        value={stats.pendingInvites}
        color="blue-500"
        onClick={() => navigate("/manage-students?tab=pending")}
      />
    </div>
  );

  // Data-driven nudges. Rendered in the left column beside the Getting-Started
  // rail (to fill the space next to the taller checklist) when the rail is up,
  // otherwise full-width in the normal flow.
  const suggestionsBlock =
    visibleTips.length > 0 ? (
      <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-5 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-amber-700">
          <Lightbulb size={18} />
          <h3 className="text-base font-bold">{t("dashboard.tips.title", { defaultValue: "Suggestions" })}</h3>
        </div>
        <ul className="flex flex-col gap-2">
          {visibleTips.map((tip) => (
            <li key={tip.key} className="flex items-center justify-between gap-4 rounded-xl bg-white border border-amber-100 px-4 py-3">
              <span className="text-sm text-gray-700">{tip.text}</span>
              {tip.action ? (
                <button
                  type="button"
                  onClick={tip.action.onClick}
                  className="flex-shrink-0 text-xs font-semibold text-primary hover:underline whitespace-nowrap"
                >
                  {tip.action.label}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Header schoolName={stats.schoolName} adminName={stats.adminName} />

      <div className="ml-[23px] mt-6 px-8 flex flex-col gap-6">
        {/* Title + re-open control */}
        <div className="flex justify-between items-center">
          <h2 className="text-gray-900 text-2xl font-bold">{greeting}</h2>
          {!showGettingStarted ? (
            <button
              type="button"
              onClick={handleOpenGettingStarted}
              className="h-11 px-4 py-1.5 bg-white border border-blue-200 text-primary hover:bg-blue-50 rounded-lg flex items-center gap-1.5 font-medium transition-colors"
              title={t("gettingStarted.reopenTip", { defaultValue: "Show the getting-started guide" })}
            >
              {t("gettingStarted.reopen", { defaultValue: "Getting started" })}
            </button>
          ) : null}
        </div>

        {/* Above the fold: primary CTAs / stats on the left, with the
            Getting-Started checklist as a compact rail on the right. When the
            checklist is hidden, the primary block spans full width. */}
        {showGettingStarted ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-2 flex flex-col gap-6">
              {primaryBlock}
              {suggestionsBlock}
            </div>
            <div className="lg:col-span-1">
              <GettingStartedChecklist
                compact
                stats={stats}
                profileConfirmed={profileConfirmed}
                certIssued={certIssued}
                hasTeam={hasTeam}
                staffSkipped={staffSkipped}
                onDismiss={handleDismissGettingStarted}
                onConfirmProfile={handleConfirmProfile}
                onImport={() => setShowImportRosterModal(true)}
                onInvite={() => setShowInviteModal(true)}
                onShareJoin={scrollToJoin}
                onSkipTeam={handleSkipTeam}
                isAdmin={showTeamCard}
              />
            </div>
          </div>
        ) : (
          primaryBlock
        )}

        {/* Graduation cohort progress — students vs how many hold a graduation
            certificate, per year. Newest cohort first. Only shown when the
            school has students with a set graduation year. */}
        {stats.cohorts.length > 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-white p-5 flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <h3 className="text-gray-900 text-lg font-bold flex items-center gap-2">
                <GraduationCap size={18} className="text-primary" />
                {t("dashboard.cohorts.title", { defaultValue: "Graduation cohorts" })}
              </h3>
              <p className="text-sm text-gray-500">
                {t("dashboard.cohorts.subtitle", { defaultValue: "Students with a graduation certificate, by year" })}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {[...stats.cohorts]
                .sort((a, b) => b.year - a.year)
                .map((c) => {
                  const total = c.studentCount || 0;
                  const certified = c.certifiedCount || 0;
                  const pct = total > 0 ? Math.round((certified / total) * 100) : 0;
                  const remaining = Math.max(0, total - certified);
                  const complete = remaining === 0;
                  return (
                    <div key={c.year} className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-semibold text-gray-900">
                          {t("dashboard.cohorts.classOf", { defaultValue: "Class of {{year}}", year: c.year })}
                        </span>
                        <span className="text-gray-500">
                          {t("dashboard.cohorts.progress", {
                            defaultValue: "{{certified}} of {{count}} certified",
                            certified,
                            count: total,
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${complete ? "bg-emerald-500" : "bg-primary"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {complete ? (
                          <span className="text-xs font-semibold text-emerald-600 whitespace-nowrap inline-flex items-center gap-1">
                            <BadgeCheck size={13} />
                            {t("dashboard.cohorts.allCertified", { defaultValue: "All certified" })}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate("/certificates")}
                            className="text-xs font-semibold text-primary hover:underline whitespace-nowrap"
                          >
                            {t("dashboard.cohorts.issueRemaining", { defaultValue: "Issue {{count}} more", count: remaining })}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        ) : null}

        {/* Smart nudges — full width here only when the Getting-Started rail
            isn't up; while it is, this moves into the left column beside it. */}
        {!showGettingStarted ? suggestionsBlock : null}

        {/* Student engagement — hidden until there's data (no wall of zeros). */}
        {!isEmptySchool ? (
        <div className="rounded-2xl border border-gray-100 bg-white p-5 flex flex-col gap-4">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h3 className="text-gray-900 text-lg font-bold">
              {t("manageStudents.engagement.title", { defaultValue: "Student engagement" })}
            </h3>
            <p className="text-sm text-gray-500">
              {t("manageStudents.engagement.subtitle", { defaultValue: "How your students are using Hypebook" })}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <EngagementStat
              icon={Award}
              tone="blue"
              label={t("manageStudents.engagement.achievements", { defaultValue: "Achievements" })}
              value={stats.totalAchievements.toLocaleString()}
              sub={t("manageStudents.engagement.acrossStudents", { defaultValue: "across all students" })}
              onClick={() => navigate("/manage-students?tab=engagement")}
            />
            <EngagementStat
              icon={ShieldCheck}
              tone="emerald"
              label={t("manageStudents.engagement.verified", { defaultValue: "Verified" })}
              value={stats.verifiedAchievements.toLocaleString()}
              sub={
                stats.totalAchievements > 0
                  ? `${verifiedPct}% ${t("manageStudents.engagement.ofAchievements", { defaultValue: "of achievements" })}`
                  : t("manageStudents.engagement.noneYet", { defaultValue: "none yet" })
              }
              onClick={() => navigate("/manage-students?tab=engagement&filter=not_verified")}
            />
            <EngagementStat
              icon={MessageCircle}
              tone="amber"
              label={t("manageStudents.engagement.hypes", { defaultValue: "Hypes received" })}
              value={stats.hypesReceived.toLocaleString()}
              onClick={() => navigate("/manage-students?tab=engagement")}
            />
            <EngagementStat
              icon={Sparkles}
              tone="violet"
              label={t("manageStudents.engagement.hypepages", { defaultValue: "Hypepages built" })}
              value={stats.studentsWithHypepage.toLocaleString()}
              sub={t("manageStudents.engagement.ofStudents", { defaultValue: "of {{total}} students", total: stats.totalStudents })}
              onClick={() => navigate("/manage-students?tab=engagement&filter=no_hypepage")}
            />
            <EngagementStat
              icon={FileText}
              tone="blue"
              label={t("manageStudents.engagement.resumes", { defaultValue: "Resumes" })}
              value={stats.studentsWithResume.toLocaleString()}
              sub={t("manageStudents.engagement.ofStudents", { defaultValue: "of {{total}} students", total: stats.totalStudents })}
              onClick={() => navigate("/manage-students?tab=engagement&filter=no_resume")}
            />
            <EngagementStat
              icon={UserMinus}
              tone="amber"
              label={t("manageStudents.engagement.yetToStart", { defaultValue: "Yet to start" })}
              value={yetToStart.toLocaleString()}
              sub={t("manageStudents.engagement.zeroAchievements", { defaultValue: "no achievements yet" })}
              onClick={() => navigate("/manage-students?tab=engagement&filter=yet_to_start")}
            />
          </div>
        </div>
        ) : null}

        {/* Team summary — ADMIN+ only. Kept secondary (below student stats)
            so the dashboard stays student-first. Data comes from the
            memberships/invites already fetched for the getting-started signal,
            so no extra request. */}
        {showTeamCard ? (
          <div className="rounded-2xl border border-gray-100 bg-white p-5 flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <h3 className="text-gray-900 text-lg font-bold">
                {t("dashboard.team.title", {
                  defaultValue: "{{schoolName}} Team",
                  schoolName: stats.schoolName || t("dashboard.team.schoolFallback", { defaultValue: "School" }),
                })}
              </h3>
              <button
                type="button"
                onClick={() => navigate("/manage-staff")}
                className="text-sm font-semibold text-primary hover:underline"
              >
                {t("dashboard.team.manage", { defaultValue: "Manage team" })}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-gray-900">{activeStaff.length}</span>
                <span className="text-sm font-medium text-gray-700">
                  {t("dashboard.team.activeStaff", { defaultValue: "active staff" })}
                </span>
              </div>
              {pendingStaffCount > 0 ? (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                  {t("dashboard.team.pending", { defaultValue: "{{count}} pending invite(s)", count: pendingStaffCount })}
                </span>
              ) : null}
            </div>

            {activeStaff.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {STAFF_ROLE_LABELS.map(([role, labelKey]) => {
                  const count = roleCounts[role] || 0;
                  if (!count) return null;
                  return (
                    <span
                      key={role}
                      className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 border border-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700"
                    >
                      {t(labelKey)}
                      <span className="text-gray-300">·</span>
                      <span className="font-semibold text-gray-900">{count}</span>
                    </span>
                  );
                })}
              </div>
            ) : null}

            <div>
              <button
                type="button"
                onClick={() => setShowInviteStaffModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600 transition-colors"
              >
                <UserPlus size={15} />
                {t("manageStudents.inviteStaff", { defaultValue: "Invite Staff" })}
              </button>
            </div>
          </div>
        ) : null}

        {/* School join code */}
        {schoolJoinInfo ? (
          <div
            id="school-join"
            className="rounded-2xl border border-blue-100 bg-blue-50/40 p-5 flex flex-col md:flex-row gap-6 md:items-center md:justify-between scroll-mt-6"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-blue-700">{t("manageStudents.schoolJoinCode")}</p>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <p className="text-2xl font-extrabold tracking-wider text-gray-900">{schoolJoinInfo.schoolCode}</p>
                <button
                  type="button"
                  onClick={() => copyToClipboard(schoolJoinInfo.schoolCode, t("manageStudents.schoolCode"))}
                  className="px-3 py-1.5 text-xs font-semibold rounded-md bg-white border border-blue-200 text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  {t("manageStudents.copyCode")}
                </button>
              </div>

              <p className="text-sm font-semibold text-blue-700 mt-4">{t("manageStudents.joinUrl")}</p>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <p className="text-sm text-gray-700 break-all">{schoolJoinInfo.joinUrl}</p>
                <button
                  type="button"
                  onClick={() => copyToClipboard(schoolJoinInfo.joinUrl, t("manageStudents.joinUrl"))}
                  className="px-3 py-1.5 text-xs font-semibold rounded-md bg-white border border-blue-200 text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  {t("manageStudents.copyUrl")}
                </button>
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 bg-white border border-blue-100 rounded-xl p-3 w-fit">
              <QRCodeSVG value={schoolJoinInfo.joinUrl} size={112} includeMargin />
              <p className="text-xs text-gray-600 font-medium">{t("manageStudents.scanToJoin", { schoolName: schoolJoinInfo.schoolName })}</p>
            </div>
          </div>
        ) : null}
      </div>

      {/* First-run welcome — gated on statsLoaded so the school name is ready. */}
      {showWelcome && statsLoaded && (
        <WelcomeScreen
          schoolName={stats.schoolName}
          onStart={handleWelcomeStart}
          onSkip={dismissWelcome}
          isAdmin={showTeamCard}
        />
      )}
      {showInviteModal && <InviteStudentModal onClose={() => setShowInviteModal(false)} onConfirm={handleInviteStudent} />}
      {showInviteStaffModal && <InviteStaffModal onClose={() => setShowInviteStaffModal(false)} onConfirm={handleInviteStaff} />}
      {showImportRosterModal && (
        <ImportRosterModal
          onClose={() => setShowImportRosterModal(false)}
          onImportComplete={() => {
            void fetchStats();
          }}
        />
      )}
    </div>
  );
}
